
import React from 'react';
import styles from './_finance-vehicle-sale-editor.module.scss';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { Input, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import {
  ApplicationDownPaymentResponse,
  carletonInsuranceCoverageTypes,
  divide,
  FinanceLoanClass, FinanceQuote,
  hasValue,
  multiply, NoScrollNumericTextBox,
  toDollars,
  DatePickerWithYearSanitizer
} from "@amsanalytics/react-shared-library";
import {IApplication} from "../../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../../Interfaces/Application/IApplicationQuote";
import {IConfiguration} from "../../../../../Data/ConfigurationData";
import {FinanceQuoteValues} from "../../../../../utils/LoanCalcUtils";
import {
  getBackgroundColorForPotentialSolveForFields,
  getFinanceQuoteErrorMessages
} from "../../../../../utils/quoteUtils";
import QuoterError from "../../../QuoterError/QuoterError";
import {getFeeAmountForCarletonInsuranceCoverageType, getTotalDownPaymentAmountPaid} from "../../../../../utils/utils";
import {Tooltip} from "@progress/kendo-react-tooltip";

export interface FinanceVehicleSaleEditorProps {
  onChangeSingleField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], applicationQuoteId?: string) => void;
  currentApp: IApplication;
  selectedFinanceQuote: FinanceQuote;
  applicationFinanceErrors: IRuleViolation[];
  configuration: IConfiguration;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  financeQuoteValues: FinanceQuoteValues;
  selectedFinanceLoanClass: FinanceLoanClass | undefined;
  applicationDownPaymentResponses: ApplicationDownPaymentResponse[];
}

export default function FinanceVehicleSaleEditor (props:  FinanceVehicleSaleEditorProps) {
  const errorMessages = getFinanceQuoteErrorMessages(props.applicationFinanceErrors);
  const { applicationDownPaymentResponses } = props;
  const totalDownPaymentAmountPaid = getTotalDownPaymentAmountPaid(applicationDownPaymentResponses) ?? 0;
  const minCashDownAllowed = Math.max(totalDownPaymentAmountPaid, 0);
  const cashDownError = minCashDownAllowed > props.selectedFinanceQuote.downPayment
    ? `Cash Down must be at least ${toDollars(minCashDownAllowed)}`
    : undefined;
  


  const propertyFeeAmount = getFeeAmountForCarletonInsuranceCoverageType(props.selectedFinanceLoanClass, props.selectedFinanceQuote, carletonInsuranceCoverageTypes.propertyInsurance);
  const motorClubFeeAmount = getFeeAmountForCarletonInsuranceCoverageType(props.selectedFinanceLoanClass, props.selectedFinanceQuote, carletonInsuranceCoverageTypes.motorClub);

  const totalAddons = propertyFeeAmount
    + motorClubFeeAmount
    + (props.selectedFinanceQuote.addPremium ?? 0)
    + (props.selectedFinanceQuote.ahPremium ?? 0)
    + (props.selectedFinanceQuote.iuiPremium ?? 0)
    + (props.selectedFinanceQuote.dlPremium ?? 0)
    + (props.selectedFinanceQuote.tpdPremium ?? 0);
  
  const stateTaxId = props.selectedFinanceLoanClass?.financeLoanClassFinanceTaxes
    .find(ft => ft.description.toUpperCase() === "STATE TAX")
    ?.id;
  const stateTaxRate = props.selectedFinanceQuote.financeQuoteTaxes
    .find(qt => qt.financeLoanClassFinanceTaxId === stateTaxId)
    ?.rate;
  // Deferred payment amount * (state tax rate / 100)
  const deferredTaxOnDeferredPayments = multiply(props.financeQuoteValues.totalDeferredPayments, divide(stateTaxRate ?? 0, 100, 8), 2);
  
  return (
    <div className={styles.financeVehicleSaleEditorContainer}>
      <div className={styles.sectionItem}>
        <DatePickerWithYearSanitizer
          disabled={props.financeQuoteFieldIsDisabled("contractDate")}
          valid={!hasValue(errorMessages.contractDate)}
          label={'Closing Date'}
          value={new Date(props.selectedFinanceQuote.contractDate)}
          onChange={(event: DatePickerChangeEvent) => props.onChangeSingleField('contractDate', event.value ?? new Date())}
        />
        <QuoterError message={errorMessages.contractDate} />
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          spinners={false}
          format="c2"
          readOnly={props.financeQuoteFieldIsDisabled("sellingPrice")}
          valid={!hasValue(errorMessages.sellingPrice)}
          label={'Selling Price'}
          value={props.selectedFinanceQuote.sellingPrice}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('sellingPrice', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("sellingPrice", props.selectedFinanceQuote.solveFor)}
        />
        <QuoterError message={errorMessages.sellingPrice} />
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          spinners={false}
          format="c2"
          readOnly={props.financeQuoteFieldIsDisabled("downPayment")}
          valid={!hasValue(errorMessages.downPayment)}
          label={'Cash Down'}
          value={props.selectedFinanceQuote.downPayment}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('downPayment', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("downPayment", props.selectedFinanceQuote.solveFor)}
          step={100}
        />
        <QuoterError message={errorMessages.downPayment} />
        <QuoterError message={cashDownError} />
      </div>
      {/*<div className={styles.sectionItem}>*/}
      {/*  <Input*/}
      {/*    readOnly // ={props.financeQuoteFieldIsDisabled("balloon")}*/}
      {/*    valid={!hasValue(errorMessages.balloon)}*/}
      {/*    label={'Balloon'}*/}
      {/*    value={props.selectedFinanceQuote.balloon}*/}
      {/*    type='number'*/}
      {/*    onChange={(e: InputChangeEvent) => props.onChangeSingleField('balloon', Number(e.target.value))}*/}
      {/*  />*/}
      {/*  <QuoterError message={errorMessages.balloon} />*/}
      {/*</div>*/}
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalDeferredDown)}
          label={'Total Deferred Down'}
          value={toDollars(props.financeQuoteValues.totalDeferredPayments)}
          type='text'
        />
        <QuoterError message={errorMessages.totalDeferredDown} />
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalFees)}
          label={'Total Fees'}
          value={toDollars(props.financeQuoteValues.totalFees)}
          type='text'
        />
        <QuoterError message={errorMessages.totalFees} />
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalTaxes)}
          label={'Total Taxes'}
          type='text'
          value={toDollars(props.selectedFinanceQuote.tax)}
        />
        <QuoterError message={errorMessages.totalTaxes} />
      </div>
      {props.selectedFinanceLoanClass?.useDeferredSalesTax && 
        <>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              label={'Deferred Tax'}
              type='text'
              value={toDollars(props.selectedFinanceQuote.taxDeferred)}
            />
            <QuoterError message={errorMessages.totalTaxes} />
          </div>
          <div className={styles.sectionItem}>
            <Input
              readOnly
              label={'Deferred Tax on Deferred Down'}
              type='text'
              value={toDollars(deferredTaxOnDeferredPayments)}
            />
            <QuoterError message={errorMessages.totalTaxes} />
          </div>
        </>
      }
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalAddons)}
          label={'Total Addons'}
          type='text'
          value={toDollars(totalAddons)}
        />
        <QuoterError message={errorMessages.totalAddons} />
      </div>
      <Tooltip anchorElement="target" position="top" parentTitle={true}>
        <div className={styles.sectionItem} title="Trade in allowance is set on the trade-in vehicle editor">
          <NoScrollNumericTextBox
            spinners={false}
            format="c2"
            readOnly={true}
            valid={!hasValue(errorMessages.tradeInValue)}
            label={'Trade In Allowance'}
            value={props.selectedFinanceQuote.tradeInValue ?? 0}
          />
          <QuoterError message={errorMessages.tradeInValue} />
        </div>
      </Tooltip>
      <Tooltip anchorElement="target" position="top" parentTitle={true}>
        <div className={styles.sectionItem} title="Trade in lien is set on the trade-in vehicle editor">
          <NoScrollNumericTextBox
            spinners={false}
            format="c2"
            readOnly={true}
            valid={!hasValue(errorMessages.tradeInPayoff)}
            label={'Trade In Lien'}
            value={props.selectedFinanceQuote.tradeInPayoff ?? 0}
          />
          <QuoterError message={errorMessages.tradeInPayoff} />
        </div>
      </Tooltip>
      {/*<div className={styles.sectionItem}>*/}
      {/*  <Input*/}
      {/*    label={'Total'}*/}
      
      {/*    type='number'*/}
      {/*    onChange={(e: InputChangeEvent) => {*/}
      
      {/*    }}*/}
      {/*  />*/}
      {/*</div>*/}
    </div>
  );
}
